import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as R from 'ramda';
import { AuthContext } from 'authentication/AuthContext';
import usePersistentFilters from 'hooks/usePersistentFilters';
import {
  useGetCarrierAuthCarriersQuery,
  useGetCarrierAuthLocationsQuery,
} from './carrierAuthorizationsEndpoints';

const INITIAL_FILTER_STATE = {
  affiliateValue: null,
  carriers: [],
  insuranceExpiration: 'All',
  locations: [],
};

const INSURANCE_OPTIONS = ['All', 'Current', 'Expiring', 'Expired'];

/**
 * Custom hook to manage carrier authorization filter state
 * Implements draft/applied pattern for better UX
 */
const useCarrierAuthFilters = () => {
  // Affiliate options filtered by supplier role, sorted by IDN ascending
  const { affiliates = [] } = useContext(AuthContext);

  const affiliateOptions = useMemo(
    () => affiliates.filter(affiliate => affiliate?.supplier),
    [affiliates]
  );

  const sortedAffiliateOptions = useMemo(
    () =>
      [...affiliateOptions].sort((a, b) =>
        (a.customerIdName ?? '').localeCompare(b.customerIdName ?? '', undefined, { numeric: true })
      ),
    [affiliateOptions]
  );

  // Map affiliate options to display labels
  const affiliateDisplayOptions = useMemo(
    () =>
      sortedAffiliateOptions.map(a => ({
        ...a,
        label: a.customerNameLong,
        value: `${a.customerIdName}-${a.dataCompanyId}`,
      })),
    [sortedAffiliateOptions]
  );

  const showAffiliateFilter = affiliateDisplayOptions.length > 1;

  const [persistedFilters, setPersistedFilters] = usePersistentFilters(
    'carrierAuthorizations',
    INITIAL_FILTER_STATE
  );

  // Resolve the selected affiliate object from persisted value or default to first
  const resolveAffiliate = useCallback(
    value => {
      const match = affiliateDisplayOptions.find(a => a.value === value);
      return match || affiliateDisplayOptions[0] || null;
    },
    [affiliateDisplayOptions]
  );

  const selectedAffiliate = useMemo(
    () => resolveAffiliate(persistedFilters.affiliateValue),
    [resolveAffiliate, persistedFilters.affiliateValue]
  );

  // Draft (pending) filters — what user edits before clicking Apply
  const [draftFilters, setDraftFilters] = useState({
    carriers: persistedFilters.carriers || [],
    insuranceExpiration: persistedFilters.insuranceExpiration || 'All',
    locations: persistedFilters.locations || [],
    search: '',
  });

  // Applied filter state (what is actually used in API queries)
  const [appliedFilters, setAppliedFilters] = useState({
    affiliate: selectedAffiliate,
    carriers: persistedFilters.carriers || [],
    insuranceExpiration: persistedFilters.insuranceExpiration || 'All',
    locations: persistedFilters.locations || [],
    search: '',
  });

  // Sync applied affiliate when selectedAffiliate changes
  useEffect(() => {
    setAppliedFilters(prev => ({ ...prev, affiliate: selectedAffiliate }));
  }, [selectedAffiliate]);

  // Carrier selector query
  const carrierQueryParams = useMemo(() => {
    if (!selectedAffiliate?.customerIdName) return null;
    return {
      supplier: {
        companyCustomerIdName: selectedAffiliate.customerIdName,
        dataCompanyId: selectedAffiliate.dataCompanyId,
      },
    };
  }, [selectedAffiliate]);

  const { data: carrierOptionsRaw = [], isFetching: carriersLoading } =
    useGetCarrierAuthCarriersQuery({ params: carrierQueryParams }, { skip: !carrierQueryParams });

  // Map carriers
  const carrierOptions = useMemo(
    () =>
      [...carrierOptionsRaw].map(c => ({
        ...c,
        label: c.carrierName,
      })),
    [carrierOptionsRaw]
  );

  // Location selector query
  const locationQueryParams = useMemo(() => {
    if (!selectedAffiliate?.customerIdName) return null;
    return {
      dataCompanyId: selectedAffiliate.dataCompanyId,
    };
  }, [selectedAffiliate]);

  const { data: locationOptionsRaw = [], isFetching: locationsLoading } =
    useGetCarrierAuthLocationsQuery(
      { params: locationQueryParams },
      { skip: !locationQueryParams }
    );

  // Map locations
  const locationOptions = useMemo(
    () =>
      [...locationOptionsRaw].map(l => ({
        ...l,
        label: l.locationDisplayName,
      })),
    [locationOptionsRaw]
  );

  const hasChanges = useMemo(() => {
    const draftCarrierIds = (draftFilters.carriers || []).map(c => c.carrierId ?? c.value).sort();
    const appliedCarrierIds = (appliedFilters.carriers || [])
      .map(c => c.carrierId ?? c.value)
      .sort();
    const draftLocationIds = (draftFilters.locations || [])
      .map(l => l.locationId ?? l.value)
      .sort();
    const appliedLocationIds = (appliedFilters.locations || [])
      .map(l => l.locationId ?? l.value)
      .sort();

    return (
      !R.equals(draftCarrierIds, appliedCarrierIds) ||
      !R.equals(draftLocationIds, appliedLocationIds) ||
      draftFilters.insuranceExpiration !== appliedFilters.insuranceExpiration
    );
  }, [draftFilters, appliedFilters]);

  const isOnDefaultState =
    !draftFilters.carriers.length &&
    draftFilters.insuranceExpiration === 'All' &&
    !draftFilters.locations.length &&
    !draftFilters.search;

  const setAffiliateFilter = useCallback(
    value => {
      // Changing affiliate resets all other filters
      const resetState = {
        carriers: [],
        insuranceExpiration: 'All',
        locations: [],
        search: '',
      };
      setDraftFilters(resetState);
      setAppliedFilters({
        affiliate: value,
        ...resetState,
      });
      setPersistedFilters({
        affiliateValue: value?.value ?? null,
        carriers: [],
        insuranceExpiration: 'All',
        locations: [],
      });
    },
    [setPersistedFilters]
  );

  const setCarrierFilter = useCallback(
    value => setDraftFilters(prev => ({ ...prev, carriers: value || [] })),
    []
  );

  const setLocationFilter = useCallback(
    value => setDraftFilters(prev => ({ ...prev, locations: value || [] })),
    []
  );

  const setInsuranceExpiration = useCallback(
    value => setDraftFilters(prev => ({ ...prev, insuranceExpiration: value || 'All' })),
    []
  );

  const setSearch = useCallback(value => setDraftFilters(prev => ({ ...prev, search: value })), []);

  const handleApply = useCallback(() => {
    setAppliedFilters(prev => ({
      ...prev,
      carriers: draftFilters.carriers,
      insuranceExpiration: draftFilters.insuranceExpiration,
      locations: draftFilters.locations,
      search: draftFilters.search,
    }));
    setPersistedFilters({
      affiliateValue: selectedAffiliate?.value ?? null,
      carriers: draftFilters.carriers,
      insuranceExpiration: draftFilters.insuranceExpiration,
      locations: draftFilters.locations,
    });
  }, [draftFilters, selectedAffiliate, setPersistedFilters]);

  const handleReset = useCallback(() => {
    const resetState = {
      carriers: [],
      insuranceExpiration: 'All',
      locations: [],
      search: '',
    };
    setDraftFilters(resetState);
    setAppliedFilters(prev => ({
      ...prev,
      ...resetState,
    }));
    setPersistedFilters({
      affiliateValue: selectedAffiliate?.value ?? null,
      carriers: [],
      insuranceExpiration: 'All',
      locations: [],
    });
  }, [selectedAffiliate, setPersistedFilters]);

  return {
    affiliateDisplayOptions,
    appliedFilters,
    carrierOptions,
    carriersLoading,
    draftFilters,
    handleApply,
    handleReset,
    hasChanges,
    insuranceOptions: INSURANCE_OPTIONS,
    isOnDefaultState,
    locationOptions,
    locationsLoading,
    selectedAffiliate,
    setAffiliateFilter,
    setCarrierFilter,
    setInsuranceExpiration,
    setLocationFilter,
    setSearch,
    showAffiliateFilter,
  };
};

export default useCarrierAuthFilters;
